import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  GripVertical, 
  Eye, 
  EyeOff, 
  Upload, 
  X, 
  Play,
  Film,
  Loader2,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [draggedVideo, setDraggedVideo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    public_id: '',
    duration: null
  });
  
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchVideos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/videos?include_hidden=true`, {
        headers: getAuthHeaders()
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/x-matroska'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid video file (MP4, MOV, WebM, AVI, MKV)');
      return;
    }

    // Validate file size (max 100MB for direct upload)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast.error('Video file is too large. Maximum size is 100MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create form data for upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // Upload to Cloudinary via backend
      const response = await axios.post(
        `${API}/cloudinary/video-upload?folder=videos`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      const { url, public_id, thumbnail_url, duration } = response.data;
      
      setFormData(prev => ({
        ...prev,
        video_url: url,
        public_id: public_id,
        thumbnail_url: thumbnail_url || '',
        duration: duration || null
      }));

      toast.success('Video uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.video_url || !formData.title) {
      toast.error('Please upload a video and enter a title');
      return;
    }

    try {
      if (editingVideo) {
        // Update existing video
        await axios.put(
          `${API}/videos/${editingVideo.id}`,
          {
            title: formData.title,
            description: formData.description || null,
            thumbnail_url: formData.thumbnail_url || null
          },
          { headers: getAuthHeaders() }
        );
        toast.success('Video updated successfully');
      } else {
        // Create new video
        await axios.post(
          `${API}/videos`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Video added successfully');
      }
      
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Failed to save video');
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Are you sure you want to delete "${video.title}"?`)) return;

    try {
      await axios.delete(`${API}/videos/${video.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const handleToggleVisibility = async (video) => {
    try {
      await axios.put(
        `${API}/videos/${video.id}`,
        { is_visible: !video.is_visible },
        { headers: getAuthHeaders() }
      );
      toast.success(`Video ${video.is_visible ? 'hidden' : 'shown'}`);
      fetchVideos();
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      video_url: video.video_url,
      thumbnail_url: video.thumbnail_url || '',
      public_id: video.public_id,
      duration: video.duration
    });
    setShowUploadModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      video_url: '',
      thumbnail_url: '',
      public_id: '',
      duration: null
    });
    setEditingVideo(null);
    setShowUploadModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop reordering
  const handleDragStart = (e, video) => {
    setDraggedVideo(video);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetVideo) => {
    e.preventDefault();
    if (!draggedVideo || draggedVideo.id === targetVideo.id) return;

    const newVideos = [...videos];
    const draggedIndex = newVideos.findIndex(v => v.id === draggedVideo.id);
    const targetIndex = newVideos.findIndex(v => v.id === targetVideo.id);
    
    newVideos.splice(draggedIndex, 1);
    newVideos.splice(targetIndex, 0, draggedVideo);
    
    setVideos(newVideos);
    setDraggedVideo(null);

    // Save new order
    try {
      await axios.put(
        `${API}/videos/reorder`,
        newVideos.map(v => v.id),
        { headers: getAuthHeaders() }
      );
      toast.success('Videos reordered');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to save order');
      fetchVideos(); // Revert on error
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#E85D75]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-semibold text-stone-800">Video Management</h2>
          <p className="text-stone-500 text-sm mt-1">Upload and manage kitten videos</p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="bg-[#E85D75] hover:bg-[#D64560] text-white"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </Button>
      </div>

      {/* Video Grid */}
      {videos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Film className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-800 mb-2">No videos yet</h3>
          <p className="text-stone-500 mb-6">Upload your first kitten video to get started</p>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-[#E85D75] hover:bg-[#D64560] text-white"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Video
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video) => (
            <div
              key={video.id}
              draggable
              onDragStart={(e) => handleDragStart(e, video)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, video)}
              className={`bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md transition-all ${
                !video.is_visible ? 'opacity-60' : ''
              } ${draggedVideo?.id === video.id ? 'ring-2 ring-[#E85D75]' : ''}`}
              data-testid={`video-card-${video.id}`}
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-stone-100">
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="w-12 h-12 text-stone-300" />
                  </div>
                )}
                
                {/* Play indicator */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>

                {/* Duration badge */}
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {formatDuration(video.duration)}
                  </div>
                )}

                {/* Hidden badge */}
                {!video.is_visible && (
                  <div className="absolute top-2 left-2 bg-stone-800/80 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <EyeOff className="w-3 h-3" />
                    Hidden
                  </div>
                )}

                {/* Drag handle */}
                <div className="absolute top-2 right-2 cursor-grab active:cursor-grabbing p-1 bg-white/90 rounded shadow">
                  <GripVertical className="w-4 h-4 text-stone-500" />
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-medium text-stone-800 truncate">{video.title}</h3>
                {video.description && (
                  <p className="text-sm text-stone-500 mt-1 line-clamp-2">{video.description}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(video)}
                    className="flex-1"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleVisibility(video)}
                    className="px-3"
                  >
                    {video.is_visible ? (
                      <Eye className="w-4 h-4 text-green-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-stone-400" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(video)}
                    className="px-3 hover:bg-red-50 hover:border-red-200"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload/Edit Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-serif font-semibold text-stone-800">
                  {editingVideo ? 'Edit Video' : 'Add New Video'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Video Upload (only for new videos) */}
              {!editingVideo && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Video File
                  </label>
                  
                  {!formData.video_url ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#E85D75] hover:bg-rose-50/50 transition-colors"
                    >
                      {isUploading ? (
                        <div className="space-y-3">
                          <Loader2 className="w-10 h-10 animate-spin text-[#E85D75] mx-auto" />
                          <p className="text-stone-600">Uploading... {uploadProgress}%</p>
                          <div className="w-full bg-stone-200 rounded-full h-2">
                            <div 
                              className="bg-[#E85D75] h-2 rounded-full transition-all"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                          <p className="text-stone-600 font-medium">Click to upload video</p>
                          <p className="text-stone-400 text-sm mt-1">MP4, MOV, WebM (max 100MB)</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden bg-stone-100">
                      <video
                        src={formData.video_url}
                        className="w-full aspect-video object-cover"
                        controls
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, video_url: '', public_id: '', thumbnail_url: '' }))}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Uploaded
                      </div>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}

              {/* Preview for editing */}
              {editingVideo && formData.video_url && (
                <div className="rounded-xl overflow-hidden bg-stone-100">
                  <video
                    src={formData.video_url}
                    className="w-full aspect-video object-cover"
                    controls
                  />
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Title *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter video title"
                  required
                  data-testid="video-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Description (optional)
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter video description"
                  rows={3}
                  data-testid="video-description-input"
                />
              </div>

              {/* Custom Thumbnail URL */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Custom Thumbnail URL (optional)
                </label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  placeholder="https://... (leave empty for auto-generated)"
                  data-testid="video-thumbnail-input"
                />
                {formData.thumbnail_url && (
                  <img 
                    src={formData.thumbnail_url} 
                    alt="Thumbnail preview" 
                    className="mt-2 rounded-lg w-32 h-20 object-cover"
                  />
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading || (!editingVideo && !formData.video_url)}
                  className="flex-1 bg-[#E85D75] hover:bg-[#D64560] text-white"
                  data-testid="save-video-btn"
                >
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
